/**
 * The vocabulary of a listing's visibility, in one place.
 *
 * `STATUS_TONE` lived twice — identically — on the detail page and the list
 * page; the edit form's status control would have made three. AGENTS.md §7
 * rule 15: the second usage is when it gets lifted.
 *
 * Everything here is pure. The mutations that actually move a listing live in
 * `use-listing-visibility.ts`.
 */
import type { StatusTone } from "@flint/ui";

export type Visibility = "draft" | "published" | "archived";

export const STATUS_TONE: Record<Visibility, StatusTone> = {
  published: "ok",
  draft: "muted",
  archived: "bad",
};

/** Sentence-case for prose and menu items; the pills render the raw value. */
export const STATUS_LABEL: Record<Visibility, string> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

/** What choosing this state actually does, in the operator's terms. */
export const STATUS_CONSEQUENCE: Record<Visibility, string> = {
  draft: "Hidden from the store",
  published: "Visible to buyers",
  archived: "Pulled off the store",
};

/**
 * Success copy keyed on the **destination**, not the verb.
 *
 * This is load-bearing, not styling. `unpublishListing` sets `draft` from *any*
 * state, so it is also the procedure behind `archived → draft` — and the old
 * verb-keyed toast ("Product unpublished") would announce that a product which
 * was never published had been unpublished. Unreachable while the UI offered
 * only buttons; reachable the moment a destination menu does.
 */
export const STATUS_DONE: Record<Visibility, string> = {
  draft: "Moved to draft",
  published: "Product published",
  archived: "Product archived",
};

/**
 * Why this listing can't go live yet — empty means it can.
 *
 * The sentences are `publishListing`'s two guards **word for word**
 * (`ListingNotPublishableError`, `ListingMissingHsnError`), so a client
 * pre-check and a server refusal can never tell the operator two different
 * things about one rule.
 *
 * `pricedEntryCount` must be the count of entries that will actually be
 * *persisted* — i.e. `usableEntries(...).length`. Do not pass the edit form's
 * `hasPricing(v)`: it also returns true for a legacy positional `sizeRatio`
 * with no priced entries, which the server counts as zero and refuses.
 *
 * @see packages/domain/src/commerce/store/publish-listing.ts
 */
export const PRICE_BLOCKER = "Add at least one pack price before publishing.";
export const HSN_BLOCKER =
  "Add an HSN code before publishing — GST invoices for this product can't be raised without one.";

export function publishBlockers(input: {
  pricedEntryCount: number;
  hsn: string | null;
}): string[] {
  const blockers: string[] = [];
  if (input.pricedEntryCount === 0) {
    blockers.push(PRICE_BLOCKER);
  }
  if (input.hsn === null || input.hsn.trim() === "") {
    blockers.push(HSN_BLOCKER);
  }
  return blockers;
}
